import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3013/api';

// Event slideshow component - cycles through events one at a time (fills full card)
const EventSlideshow = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reset index if items change and current index is out of bounds
  useEffect(() => {
    if (items.length === 0) {
      setCurrentIndex(0);
    } else if (currentIndex >= items.length) {
      setCurrentIndex(0);
    }
  }, [items.length, currentIndex]);

  // Auto-advance slideshow
  useEffect(() => {
    if (items.length <= 1) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => {
          // Safety check - ensure we don't go out of bounds
          const next = prev + 1;
          return next >= items.length ? 0 : next;
        });
        setIsTransitioning(false);
      }, 300);
    }, 5000); // 5 seconds per item

    return () => clearInterval(interval);
  }, [items.length]);

  // Show empty state if no items
  if (!items || items.length === 0) {
    return (
      <div style={slideStyles.noEvent}>
        <span style={slideStyles.khandaIcon}>☬</span>
        <span style={slideStyles.waheguruText}>ਸਤਿਨਾਮ ਵਾਹਿਗੁਰੂ</span>
      </div>
    );
  }

  // Safety check - ensure currentIndex is valid
  const safeIndex = currentIndex < items.length ? currentIndex : 0;
  const item = items[safeIndex];

  // Extra safety - if item is somehow undefined, show empty state
  if (!item) {
    return (
      <div style={slideStyles.noEvent}>
        <span style={slideStyles.khandaIcon}>☬</span>
        <span style={slideStyles.waheguruText}>ਸਤਿਨਾਮ ਵਾਹਿਗੁਰੂ</span>
      </div>
    );
  }

  return (
    <div style={slideStyles.container}>
      {/* Counter indicator */}
      {items.length > 1 && (
        <div style={slideStyles.counter}>
          {safeIndex + 1} / {items.length}
        </div>
      )}

      <div 
        style={{
          ...slideStyles.slideContent,
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'translateX(20px)' : 'translateX(0)',
        }}
      >
        {item.type === 'holiday' ? (
          // Holiday display - fills full card
          <div style={{
            ...slideStyles.holiday,
            background: `${item.color}20`,
          }}>
            <span style={slideStyles.holidayIcon}>{item.icon}</span>
            <div style={slideStyles.holidayName}>{item.name}</div>
          </div>
        ) : (
          // Event display - fills full card
          <div style={{
            ...slideStyles.event,
            borderLeftColor: item.programTypeColor || '#F97316',
          }}>
            {/* Time/Day Badge Row */}
            <div style={slideStyles.eventHeader}>
              {item.isRecurringDaily ? (
                <>
                  <span style={slideStyles.dayBadge}>{item.dayLabel}</span>
                  <span style={slideStyles.eventTime}>{item.displayStartTime}</span>
                </>
              ) : item.isMultiDay ? (
                <>
                  <span style={slideStyles.dayBadge}>{item.dayLabel}</span>
                  {(item.dayNumber === 1 || item.dayNumber === item.totalDays) && (
                    <span style={slideStyles.eventTime}>
                      {item.dayNumber === 1 ? item.startTime : item.endTime}
                    </span>
                  )}
                </>
              ) : (
                <span style={slideStyles.eventTime}>{item.startTime}</span>
              )}
            </div>
            {/* Event Name - Big */}
            <div style={slideStyles.eventName}>{item.programTypeName || 'Event'}</div>
            {/* Booking Name */}
            <div style={slideStyles.eventBooking}>{item.bookingName}</div>
          </div>
        )}
      </div>

      {/* Dot indicators */}
      {items.length > 1 && (
        <div style={slideStyles.dots}>
          {items.map((_, i) => (
            <div 
              key={i} 
              style={{
                ...slideStyles.dot,
                background: i === safeIndex ? '#F97316' : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Styles for the slideshow component - fills full card
const slideStyles = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  counter: {
    position: 'absolute',
    top: 10,
    right: 12,
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: 700,
    background: 'rgba(0,0,0,0.3)',
    padding: '2px 8px',
    borderRadius: 10,
  },
  slideContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    transition: 'all 0.3s ease-in-out',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginTop: 10,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    transition: 'background 0.3s',
  },
  noEvent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  khandaIcon: {
    fontSize: 56,
    color: '#F97316',
    opacity: 0.8,
  },
  waheguruText: {
    fontSize: 18,
    color: '#94a3b8',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textAlign: 'center',
  },
  event: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: 8,
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    borderLeft: '5px solid #F97316',
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 8,
  },
  eventTime: {
    color: '#F97316',
    fontSize: 20,
    fontWeight: 700,
  },
  dayBadge: {
    background: '#F97316',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  eventName: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: 6,
    wordWrap: 'break-word',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  eventBooking: {
    color: '#94a3b8',
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.2,
    wordWrap: 'break-word',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  holiday: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: 8,
    borderRadius: 10,
    background: 'rgba(124, 58, 237, 0.15)',
  },
  holidayIcon: {
    fontSize: 36,
    marginBottom: 8,
  },
  holidayName: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.2,
    wordWrap: 'break-word',
  },
};

// Parse date string to local date (avoids UTC timezone issues)
// Input: ISO string like "2024-04-15T04:00:00.000Z" or "2024-04-15"
// Output: Date object in local timezone for that calendar date
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // Extract just the date part (YYYY-MM-DD) from any format
  const str = String(dateString);
  let dateOnly;
  
  if (str.includes('T')) {
    // ISO format: extract date before T
    dateOnly = str.split('T')[0];
  } else if (str.length === 10 && str.includes('-')) {
    // Already YYYY-MM-DD format
    dateOnly = str;
  } else {
    // Try parsing as-is but get local date components
    const d = new Date(str);
    if (isNaN(d.getTime())) return null;
    dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  
  // Parse YYYY-MM-DD into local timezone
  const [year, month, day] = dateOnly.split('-').map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  
  return localDate;
};

// Generate next 14 days
const getNext14Days = () => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    days.push(date);
  }
  return days;
};

// Format date helper
const formatDate = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return {
    day: days[date.getDay()],
    date: date.getDate(),
    month: months[date.getMonth()],
    dateStr: date.toDateString(),
  };
};

// Group events by date (handles multi-day and recurring daily events)
const groupEventsByDate = (events) => {
  const grouped = {};
  
  events.forEach(event => {
    if (!event.startDate) return;
    
    const startDate = parseLocalDate(event.startDate);
    if (!startDate || startDate.toDateString() === 'Invalid Date') return;
    
    const endDate = event.endDate ? parseLocalDate(event.endDate) : startDate;
    
    // Calculate total days of the event
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const isMultiDay = totalDays > 1;
    const isRecurringDaily = event.isRecurringDaily === true;
    
    // Add event to each day it spans
    for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + (dayNum - 1));
      const dateKey = currentDate.toDateString();
      
      if (!grouped[dateKey]) grouped[dateKey] = [];
      
      // For recurring daily events, use daily times
      // For continuous multi-day events, use original start/end times
      const displayStartTime = isRecurringDaily && event.dailyStartTime 
        ? event.dailyStartTime 
        : event.startTime;
      const displayEndTime = isRecurringDaily && event.dailyEndTime 
        ? event.dailyEndTime 
        : event.endTime;
      
      // Create event entry with day info for multi-day events
      const eventEntry = {
        ...event,
        type: 'event',
        isMultiDay,
        isRecurringDaily,
        dayNumber: dayNum,
        totalDays: event.totalDays || totalDays,
        displayStartTime,
        displayEndTime,
        // Show different labels based on event type
        dayLabel: isRecurringDaily 
          ? `Day ${dayNum}/${event.totalDays || totalDays}`
          : (isMultiDay ? (
              dayNum === 1 ? 'Start' : 
              dayNum === totalDays ? 'Bhog' : 
              `Day ${dayNum}`
            ) : null),
      };
      
      grouped[dateKey].push(eventEntry);
    }
  });
  
  return grouped;
};

// Group holidays by date
const groupHolidaysByDate = (holidays) => {
  const grouped = {};
  holidays.forEach(holiday => {
    if (!holiday.date) return;
    const parsedDate = parseLocalDate(holiday.date);
    if (!parsedDate) return;
    const dateKey = parsedDate.toDateString();
    if (dateKey === 'Invalid Date') return;
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push({ ...holiday, type: 'holiday' });
  });
  return grouped;
};

// Merge events and holidays
const mergeByDate = (events, holidays) => {
  const merged = { ...events };
  Object.keys(holidays).forEach(dateKey => {
    if (!merged[dateKey]) merged[dateKey] = [];
    merged[dateKey] = [...holidays[dateKey], ...merged[dateKey]];
  });
  return merged;
};

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      // No auth required - public endpoints
      const [eventsRes, holidaysRes] = await Promise.all([
        axios.get(`${API_URL}/sab/events/public-calendar`),
        axios.get(`${API_URL}/sab/sikh-holidays/public-calendar`),
      ]);
      
      setEvents(eventsRes.data || []);
      setHolidays(holidaysRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll every 60 seconds for updates
    const interval = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const days = getNext14Days();
  const groupedEvents = groupEventsByDate(events);
  const groupedHolidays = groupHolidaysByDate(holidays);
  const allItems = mergeByDate(groupedEvents, groupedHolidays);
  const today = new Date().toDateString();
  const tomorrow = new Date(Date.now() + 86400000).toDateString();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <img src="/logo.png" alt="CGS" style={styles.logo} />
        <div style={styles.headerTitle}>Upcoming Programs</div>
        <div style={styles.headerSub}>ਆਉਣ ਵਾਲੇ ਪ੍ਰੋਗਰਾਮ</div>
      </div>

      {/* Calendar Grid - 5 columns x 3 rows (14 days + 1 branding card) */}
      <div style={styles.grid}>
        {days.map((date, index) => {
          const { day, date: dateNum, month, dateStr } = formatDate(date);
          const dayItems = allItems[dateStr] || [];
          const isToday = dateStr === today;
          const isTomorrow = dateStr === tomorrow;
          const hasItems = dayItems.length > 0;
          const hasHoliday = dayItems.some(item => item.type === 'holiday');

          return (
            <div
              key={index}
              style={{
                ...styles.card,
                ...(isToday && styles.todayCard),
                ...(isTomorrow && !isToday && styles.tomorrowCard),
                ...(hasHoliday && styles.holidayCard),
                ...(!hasItems && styles.emptyCard),
              }}
            >
              {/* Date Header */}
              <div style={{
                ...styles.dateHeader,
                background: hasHoliday ? '#7c3aed' : isToday ? '#F97316' : isTomorrow ? '#3b82f6' : '#1e293b',
              }}>
                <span style={styles.dateNum}>{dateNum}</span>
                <div style={styles.dateMeta}>
                  <span style={styles.dayName}>{day}</span>
                  <span style={styles.monthName}>{month}</span>
                </div>
                {isToday && <span style={styles.badge}>TODAY</span>}
                {isTomorrow && !isToday && <span style={{...styles.badge, background: '#fff', color: '#3b82f6'}}>TMR</span>}
              </div>

              {/* Items Slideshow (cycles through all events/holidays) */}
              <EventSlideshow items={dayItems} />
            </div>
          );
        })}

        {/* Codeforce Branding Card */}
        <div style={styles.brandingCard}>
          <div style={styles.brandingContent}>
            <img 
              src="https://codeforce.ca/logo.png" 
              alt="Codeforce" 
              style={styles.brandingLogo}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div style={styles.brandingText}>
              <span style={styles.brandingTitle}>Designed & Developed by</span>
              <span style={styles.brandingCompany}>CODEFORCE</span>
              <span style={styles.brandingEmail}>info@codeforce.ca</span>
            </div>
            <div style={styles.brandingGuidance}>
              Under guidance of Cambridge Gurudwara Sahib
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ ॥ ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ ॥
      </div>

    </div>
  );
};

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    overflow: 'hidden',
    padding: 16,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 24px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    marginBottom: 14,
  },
  logo: {
    width: 48,
    height: 48,
  },
  headerTitle: {
    color: '#F97316',
    fontSize: 28,
    fontWeight: 700,
  },
  headerSub: {
    color: '#94a3b8',
    fontSize: 16,
    marginLeft: 'auto',
  },
  grid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridTemplateRows: 'repeat(3, 1fr)',
    gap: 12,
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  todayCard: {
    border: '3px solid #F97316',
    boxShadow: '0 0 20px rgba(249, 115, 22, 0.4)',
  },
  tomorrowCard: {
    border: '3px solid #3b82f6',
  },
  holidayCard: {
    border: '2px solid #7c3aed',
    boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)',
  },
  emptyCard: {
    opacity: 0.7,
  },
  dateHeader: {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: '#fff',
  },
  dateNum: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1,
  },
  dateMeta: {
    display: 'flex',
    flexDirection: 'column',
  },
  dayName: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'uppercase',
    lineHeight: 1.2,
  },
  monthName: {
    fontSize: 12,
    opacity: 0.8,
    lineHeight: 1.2,
  },
  badge: {
    marginLeft: 'auto',
    background: '#fff',
    color: '#F97316',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 4,
  },
  footer: {
    marginTop: 12,
    padding: '10px 16px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    textAlign: 'center',
    color: '#F97316',
    fontSize: 16,
  },
  loadingText: {
    color: '#fff',
    fontSize: 20,
    margin: 'auto',
  },
  brandingCard: {
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 12,
  },
  brandingLogo: {
    width: 60,
    height: 60,
    objectFit: 'contain',
    filter: 'brightness(1.1)',
  },
  brandingText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  brandingTitle: {
    color: '#94a3b8',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  brandingCompany: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 2,
  },
  brandingEmail: {
    color: '#F97316',
    fontSize: 12,
    fontWeight: 500,
  },
  brandingGuidance: {
    color: '#64748b',
    fontSize: 10,
    textAlign: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid rgba(255,255,255,0.1)',
    lineHeight: 1.3,
  },
};

export default EventCalendar;
